import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { vansAPI } from '../../lib/api';
import {
  ArrowLeft, Truck, Package, Loader2, AlertCircle, Search,
} from 'lucide-react';
import { Input } from '../../components/ui/input';

export default function TechMyVehicle() {
  const { user, assignedVanId } = useAuth();
  const navigate = useNavigate();

  const [van, setVan] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchVan = useCallback(async () => {
    if (!assignedVanId) { setLoading(false); return; }
    try {
      const [vanRes, itemsRes] = await Promise.all([
        vansAPI.get(assignedVanId),
        vansAPI.getInventory(assignedVanId),
      ]);
      setVan(vanRes.data);
      setItems(itemsRes.data || []);
    } catch (err) {
      console.error('Failed to load vehicle:', err);
    } finally {
      setLoading(false);
    }
  }, [assignedVanId]);

  useEffect(() => { fetchVan(); }, [fetchVan]);

  const filtered = search.trim()
    ? items.filter(i =>
        (i.item_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (i.item_sku || '').toLowerCase().includes(search.toLowerCase())
      )
    : items;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="tech-vehicle-loading">
        <Loader2 className="w-7 h-7 animate-spin text-blue-600" />
      </div>
    );
  }

  /* No vehicle assigned */
  if (!assignedVanId || !van) {
    return (
      <div className="flex flex-col h-full bg-slate-50" data-testid="tech-vehicle-empty">
        <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200">
          <button onClick={() => navigate('/tech/more')} className="p-1.5 -ml-1 rounded-lg active:bg-slate-100" data-testid="tech-vehicle-back-btn">
            <ArrowLeft className="w-5 h-5 text-slate-700" />
          </button>
          <h1 className="text-base font-bold text-slate-900">My Vehicle</h1>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <Truck className="w-8 h-8 text-slate-300" />
          </div>
          <p className="text-base font-bold text-slate-700 mb-1">No Vehicle Assigned</p>
          <p className="text-sm text-slate-400">Contact your admin to be assigned to a vehicle.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50" data-testid="tech-vehicle">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="flex items-center gap-3 px-4 py-3">
          <button onClick={() => navigate('/tech/more')} className="p-1.5 -ml-1 rounded-lg active:bg-slate-100" data-testid="tech-vehicle-back-btn">
            <ArrowLeft className="w-5 h-5 text-slate-700" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-bold text-slate-900 truncate">{van.name}</h1>
            {van.van_number && <p className="text-xs text-slate-400">#{van.van_number}</p>}
          </div>
          <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-bold shrink-0">
            <Package className="w-3.5 h-3.5" />
            {items.length}
          </div>
        </div>

        {/* Search */}
        {items.length > 5 && (
          <div className="px-4 pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search inventory..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 pl-9 text-sm bg-slate-50 border-slate-200"
                data-testid="tech-vehicle-search"
              />
            </div>
          </div>
        )}
      </div>

      {/* Inventory list */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="text-center py-12 px-6">
            <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-500">
              {search ? 'No items match your search' : 'No inventory on this vehicle'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((item) => {
              const qty = item.qty_on_hand ?? item.quantity ?? 0;
              const isLow = item.min_quantity != null && qty <= item.min_quantity;
              return (
                <div
                  key={item.id || item.item_id}
                  className="flex items-center gap-3 px-4 py-3 bg-white"
                  data-testid={`tech-vehicle-item-${item.id || item.item_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{item.item_name || item.name}</p>
                    {(item.item_sku || item.sku) && <p className="text-[11px] text-slate-400 truncate">SKU: {item.item_sku || item.sku}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isLow && <AlertCircle className="w-4 h-4 text-amber-500" />}
                    <span className={`text-sm font-bold tabular-nums ${isLow ? 'text-amber-600' : 'text-slate-900'}`}>
                      {qty}
                    </span>
                    {item.min_quantity != null && (
                      <span className="text-[11px] text-slate-400">/ {item.min_quantity}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
